/**
 * @unscoped — SettingsPage 의 i18n — Cycle 59.
 *
 * 사용자 영역 (SettingsPage 메인 + ProfileSection + PasswordSection + EnvironmentSection +
 * ExternalProfileSection) 의 응집 cleanup. admin 영역 3 sections 는 cycle 60 분리 —
 * cycle 55-56 의 OnboardingWizard 2-cycle cleanup 패턴 재적용.
 *
 * cycle 51 calendar-i18n / cycle 54 mail-i18n / cycle 55 onboarding-i18n /
 * cycle 57 board-i18n / cycle 58 task-i18n 통합 패턴 일관.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== SettingsPage 메인 =====
  'settings.heading': { ko: '설정', en: 'Settings', ja: '設定' },
  'settings.subtitle': {
    ko: '환경·프로필·보안을 한 곳에서 관리하세요',
    en: 'Manage your preferences, profile, and security in one place',
    ja: '環境・プロフィール・セキュリティを一括管理',
  },
  // ===== 목적별 탭 라벨 =====
  'settings.tab.general': { ko: '일반', en: 'General', ja: '一般' },
  'settings.tab.profile': { ko: '프로필', en: 'Profile', ja: 'プロフィール' },
  'settings.tab.notifications': { ko: '알림 연동', en: 'Notifications', ja: '通知連携' },
  'settings.tab.admin': { ko: '관리자', en: 'Admin', ja: '管理者' },
  'settings.tab.aiProvider': { ko: 'AI Provider', en: 'AI Provider', ja: 'AIプロバイダ' },
  'settings.tab.aiTools': { ko: 'AI 도구', en: 'AI Tools', ja: 'AIツール' },

  // ===== AI Provider 탭 =====
  'settings.aiProvider.heading': {
    ko: 'AI Provider',
    en: 'AI Provider',
    ja: 'AIプロバイダ',
  },
  'settings.aiProvider.subtitle': {
    ko: '현재 시스템에 감지된 LLM 제공자입니다. 환경변수(env) 와 CLI 도구를 자동 인식합니다.',
    en: 'LLM providers currently detected on this system. Auto-detected from environment variables (env) and CLI tools.',
    ja: '現在のシステムで検出されたLLMプロバイダです。環境変数(env)とCLIツールを自動認識します。',
  },
  'settings.aiProvider.section.env': {
    ko: '환경변수 기반 (env)',
    en: 'Environment variables (env)',
    ja: '環境変数ベース (env)',
  },
  'settings.aiProvider.section.cli': {
    ko: 'CLI 도구 기반',
    en: 'CLI tools',
    ja: 'CLIツールベース',
  },
  'settings.aiProvider.section.env.desc': {
    ko: 'API 키(.env)로 인증하는 클라우드 LLM',
    en: 'Cloud LLMs authenticated via API keys (.env)',
    ja: 'APIキー(.env)で認証するクラウドLLM',
  },
  'settings.aiProvider.section.cli.desc': {
    ko: '로컬에 설치된 CLI 도구 기반 LLM',
    en: 'LLMs via locally installed CLI tools',
    ja: 'ローカルにインストールされたCLIツールベースのLLM',
  },
  'settings.aiProvider.hero.active': { ko: '현재 활성 AI', en: 'Active AI', ja: '現在のアクティブAI' },
  'settings.aiProvider.hero.none': {
    ko: '활성화된 AI가 없습니다 — 자동 fallback 동작 중',
    en: 'No active AI — using auto fallback',
    ja: 'アクティブなAIがありません — 自動フォールバック動作中',
  },
  'settings.aiProvider.activeBadge': { ko: '활성', en: 'Active', ja: 'アクティブ' },
  'settings.aiProvider.field.baseUrl': { ko: 'Base URL', en: 'Base URL', ja: 'Base URL' },
  'settings.aiProvider.field.apiKey': { ko: 'API Key', en: 'API Key', ja: 'APIキー' },
  'settings.aiProvider.field.envFile': { ko: 'env 파일', en: 'env file', ja: 'envファイル' },
  'settings.aiProvider.field.envKeys': { ko: 'env 키', en: 'env keys', ja: 'envキー' },
  'settings.aiProvider.field.bin': { ko: '실행 경로', en: 'Binary path', ja: '実行パス' },
  'settings.aiProvider.field.version': { ko: '버전', en: 'Version', ja: 'バージョン' },
  'settings.aiProvider.empty.env': {
    ko: '감지된 환경변수 기반 provider 가 없습니다. .env 에 OPENAI_API_KEY 등을 설정하세요.',
    en: 'No env-based providers detected. Set OPENAI_API_KEY etc. in your .env.',
    ja: '環境変数ベースのプロバイダが検出されませんでした。.envにOPENAI_API_KEY等を設定してください。',
  },
  'settings.aiProvider.empty.cli': {
    ko: '감지된 CLI 도구가 없습니다.',
    en: 'No CLI tools detected.',
    ja: 'CLIツールが検出されませんでした。',
  },
  'settings.aiProvider.error.load': {
    ko: '정보를 불러오지 못했습니다.',
    en: 'Failed to load.',
    ja: '情報を読み込めませんでした。',
  },
  'settings.aiProvider.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  'settings.aiProvider.btn.activate': {
    ko: '활성화',
    en: 'Activate',
    ja: 'アクティブにする',
  },
  'settings.aiProvider.btn.deactivate': {
    ko: '비활성화',
    en: 'Deactivate',
    ja: '無効にする',
  },
  'settings.aiProvider.saving': { ko: '적용 중…', en: 'Applying…', ja: '適用中...' },
  'settings.aiProvider.btn.healthCheck': { ko: '연결 테스트', en: 'Test connection', ja: '接続テスト' },
  'settings.aiProvider.health.checking': { ko: '확인 중…', en: 'Checking…', ja: '確認中...' },
  'settings.aiProvider.health.ok': { ko: '연결 정상', en: 'Connected', ja: '接続正常' },
  'settings.aiProvider.health.fail': { ko: '연결 실패', en: 'Failed', ja: '接続失敗' },
  'settings.aiProvider.error.save': {
    ko: '선택을 저장하지 못했습니다.',
    en: 'Failed to save selection.',
    ja: '選択を保存できませんでした。',
  },
  'settings.aiProvider.scope.user': {
    ko: '내 설정',
    en: 'My selection',
    ja: '個人設定',
  },
  'settings.aiProvider.scope.global': {
    ko: '기관 기본값',
    en: 'Org default',
    ja: '機関デフォルト',
  },
  'settings.aiProvider.scope.default': {
    ko: '자동 (fallback)',
    en: 'Auto (fallback)',
    ja: '自動 (fallback)',
  },
  'settings.aiProvider.field.model': { ko: '모델', en: 'Model', ja: 'モデル' },
  'settings.aiProvider.model.useDefault': {
    ko: '기본 (env 설정 사용)',
    en: 'Default (env)',
    ja: 'デフォルト (env)',
  },
  'settings.aiProvider.model.loading': {
    ko: '모델 목록 불러오는 중…',
    en: 'Loading models…',
    ja: 'モデルを読み込み中...',
  },
  'settings.aiProvider.model.unsupported': {
    ko: 'CLI provider — 모델 선택 미지원 (CLI 기본값 사용)',
    en: 'CLI provider — model selection unsupported',
    ja: 'CLIプロバイダ — モデル選択は未対応',
  },
  'settings.aiProvider.model.empty': {
    ko: '감지된 모델이 없습니다.',
    en: 'No models detected.',
    ja: 'モデルが検出されませんでした。',
  },
  'settings.aiProvider.model.error': {
    ko: '모델 목록을 불러오지 못했습니다.',
    en: 'Failed to load models.',
    ja: 'モデルリストを読み込めませんでした。',
  },
  'settings.aiProvider.activeModel': {
    ko: '활성 모델',
    en: 'Active model',
    ja: 'アクティブモデル',
  },

  // ===== 공통 버튼 =====
  'settings.btn.save': { ko: '저장', en: 'Save', ja: '保存' },
  'settings.btn.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'settings.msg.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'settings.msg.saved_punct': { ko: '저장되었습니다.', en: 'Saved.', ja: '保存しました。' },

  // ===== ProfileSection (SET-005) =====
  'settings.profile.heading': { ko: '프로필', en: 'Profile', ja: 'プロフィール' },
  'settings.profile.login_required': {
    ko: '로그인이 필요합니다.',
    en: 'Login required.',
    ja: 'ログインが必要です。',
  },
  'settings.profile.label_name': { ko: '이름', en: 'Name', ja: '名前' },
  'settings.profile.label_position': { ko: '직책', en: 'Position', ja: '役職' },
  'settings.profile.placeholder_position': { ko: '예: 매니저', en: 'e.g. Manager', ja: '例: マネージャー' },
  'settings.profile.label_phone': { ko: '전화', en: 'Phone', ja: '電話' },
  'settings.profile.placeholder_phone': {
    ko: '예: 010-0000-0000',
    en: 'e.g. 010-0000-0000',
    ja: '例: 010-0000-0000',
  },
  'settings.profile.label_avatar': { ko: '아바타 URL', en: 'Avatar URL', ja: 'アバターURL' },
  'settings.profile.err_save': {
    ko: '저장 실패: {msg}',
    en: 'Save failed: {msg}',
    ja: '保存失敗: {msg}',
  },
  'settings.profile.err_unknown': {
    ko: '알 수 없는 오류',
    en: 'Unknown error',
    ja: '不明なエラー',
  },

  // ===== PasswordSection (SET-006) =====
  'settings.password.heading': { ko: '비밀번호 변경', en: 'Change password', ja: 'パスワード変更' },
  'settings.password.label_current': { ko: '현재 비밀번호', en: 'Current password', ja: '現在のパスワード' },
  'settings.password.label_new': { ko: '새 비밀번호', en: 'New password', ja: '新しいパスワード' },
  'settings.password.label_confirm': { ko: '새 비밀번호 확인', en: 'Confirm new password', ja: '新しいパスワード (確認)' },
  'settings.password.btn_change': { ko: '변경', en: 'Change', ja: '変更' },
  'settings.password.btn_changing': { ko: '변경 중…', en: 'Changing…', ja: '変更中...' },
  'settings.password.msg_success': {
    ko: '비밀번호가 변경되었습니다',
    en: 'Password changed',
    ja: 'パスワードが変更されました',
  },
  'settings.password.err_mismatch': {
    ko: '새 비밀번호와 확인이 일치하지 않습니다',
    en: 'New password and confirmation do not match',
    ja: '新しいパスワードと確認が一致しません',
  },
  'settings.password.err_min_length': {
    ko: '최소 {n}자 이상',
    en: 'At least {n} characters',
    ja: '最低{n}文字以上',
  },
  'settings.password.err_categories': {
    ko: '영문·숫자·특수문자 중 3종 이상 포함',
    en: 'Include 3+ of: letters / digits / symbols',
    ja: '英字・数字・記号のうち3種類以上を含めてください',
  },
  'settings.password.err_current_required': {
    ko: '현재 비밀번호를 입력하세요',
    en: 'Please enter your current password',
    ja: '現在のパスワードを入力してください',
  },
  'settings.password.err_invalid': {
    ko: '현재 비밀번호가 일치하지 않거나 비밀번호 변경이 불가한 계정입니다',
    en: 'Current password is incorrect, or this account cannot change passwords',
    ja: '現在のパスワードが一致しないか、変更不可のアカウントです',
  },
  'settings.password.err_policy': {
    ko: '비밀번호 정책 위반: {violations}',
    en: 'Password policy violated: {violations}',
    ja: 'パスワードポリシー違反: {violations}',
  },
  'settings.password.err_reused': {
    ko: '새 비밀번호가 현재 비밀번호와 동일합니다',
    en: 'New password is the same as the current password',
    ja: '新しいパスワードが現在のパスワードと同じです',
  },
  'settings.password.err_change': {
    ko: '변경 실패: {message}',
    en: 'Change failed: {message}',
    ja: '変更失敗: {message}',
  },

  // ===== EnvironmentSection (SET-001~004) =====
  'settings.env.heading': { ko: '환경설정', en: 'Preferences', ja: '環境設定' },
  'settings.env.loading': { ko: '로드 중…', en: 'Loading…', ja: '読み込み中...' },
  'settings.env.err_load': {
    ko: '설정 로드 실패: {msg}',
    en: 'Failed to load settings: {msg}',
    ja: '設定読み込み失敗: {msg}',
  },
  'settings.env.err_save': {
    ko: '저장 실패: {msg}',
    en: 'Save failed: {msg}',
    ja: '保存失敗: {msg}',
  },
  'settings.env.label_theme': { ko: '테마 색상', en: 'Theme color', ja: 'テーマカラー' },
  'settings.env.theme.ocean_blue': { ko: '오션 블루', en: 'Ocean Blue', ja: 'オーシャンブルー' },
  'settings.env.theme.sunset_orange': { ko: '선셋 오렌지', en: 'Sunset Orange', ja: 'サンセットオレンジ' },
  'settings.env.theme.forest_green': { ko: '포레스트 그린', en: 'Forest Green', ja: 'フォレストグリーン' },
  'settings.env.theme.royal_purple': { ko: '로열 퍼플', en: 'Royal Purple', ja: 'ロイヤルパープル' },
  'settings.env.theme.rose_pink': { ko: '로즈 핑크', en: 'Rose Pink', ja: 'ローズピンク' },
  'settings.env.label_dark_mode': { ko: '다크 모드', en: 'Dark mode', ja: 'ダークモード' },
  'settings.env.label_language': { ko: '언어', en: 'Language', ja: '言語' },
  // cycle 45 ja 추가 시 LANGUAGES list 갱신 누락 → cycle 59 보강 (3 lang 일관).
  'settings.env.lang.ko': { ko: '한국어', en: 'Korean', ja: '韓国語' },
  'settings.env.lang.en': { ko: '영어', en: 'English', ja: '英語' },
  'settings.env.lang.ja': { ko: '일본어', en: 'Japanese', ja: '日本語' },
  'settings.env.label_notif_chat': { ko: '채팅 알림', en: 'Chat notifications', ja: 'チャット通知' },
  'settings.env.label_notif_mail': { ko: '메일 알림', en: 'Mail notifications', ja: 'メール通知' },
  'settings.env.label_notif_calendar': { ko: '캘린더 알림', en: 'Calendar notifications', ja: 'カレンダー通知' },
  'settings.env.label_notif_task': { ko: '태스크 알림', en: 'Task notifications', ja: 'タスク通知' },
  'settings.env.msg_pending': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },

  // ===== ExternalProfileSection =====
  'settings.external.heading': {
    ko: '외부 알림 연동',
    en: 'External notification integrations',
    ja: '外部通知連携',
  },
  'settings.external.desc': {
    ko: 'Slack · 외부 이메일 · 모바일 푸시 매핑. 비워두면 해당 채널로 알림이 가지 않습니다.',
    en: 'Slack / external email / mobile push mapping. Leave blank to disable that channel.',
    ja: 'Slack・外部メール・モバイルプッシュのマッピング。空欄にすると該当チャネルには通知されません。',
  },
  'settings.external.last_updated': {
    ko: ' 마지막 갱신: {date}',
    en: ' Last updated: {date}',
    ja: ' 最終更新: {date}',
  },
  // ⚠️ test 의존성 — 'Slack User ID' / '외부 이메일' / 'FCM Device Token' 의 ko 텍스트는
  // ExternalProfileSection.test.tsx 의 screen.getByLabelText 가 사용. ko 텍스트 보존.
  'settings.external.label_slack': { ko: 'Slack User ID', en: 'Slack User ID', ja: 'Slack ユーザーID' },
  'settings.external.placeholder_slack': {
    ko: 'U01ABC2345 (Slack 워크스페이스의 본인 user ID)',
    en: 'U01ABC2345 (your Slack workspace user ID)',
    ja: 'U01ABC2345 (Slackワークスペースの自分のユーザーID)',
  },
  'settings.external.label_email': { ko: '외부 이메일', en: 'External email', ja: '外部メール' },
  'settings.external.placeholder_email': { ko: 'me@external.com', en: 'me@external.com', ja: 'me@external.com' },
  'settings.external.label_fcm': { ko: 'FCM Device Token', en: 'FCM Device Token', ja: 'FCM デバイストークン' },
  'settings.external.placeholder_fcm': {
    ko: '모바일 앱이 발급한 FCM token',
    en: 'FCM token issued by your mobile app',
    ja: 'モバイルアプリが発行したFCMトークン',
  },
  'settings.external.err_load': {
    ko: '외부 매핑 로드 실패: {msg}',
    en: 'Failed to load external mapping: {msg}',
    ja: '外部マッピング読み込み失敗: {msg}',
  },
  'settings.external.err_save': {
    ko: '저장 실패: {msg}',
    en: 'Save failed: {msg}',
    ja: '保存失敗: {msg}',
  },

  // ===== Cycle 60 — admin sections (AdminExternal + AdminFaq + AdminBulkImport) =====

  // ----- AdminExternalProfileSection -----
  'settings.admin_external.heading': {
    ko: 'ADMIN — 다른 사용자 외부 매핑',
    en: 'ADMIN — Other users\' external mapping',
    ja: 'ADMIN — 他ユーザーの外部マッピング',
  },
  'settings.admin_external.desc': {
    ko: 'ADMIN 권한 — 본인 외 다른 사용자 매핑 편집. audit 에 byAdmin=true 영구 기록.',
    en: 'ADMIN privileges — edit mappings for other users. byAdmin=true is permanently recorded in audit.',
    ja: 'ADMIN 権限 — 自分以外のユーザーマッピングを編集。audit に byAdmin=true を永続記録。',
  },
  'settings.admin_external.label_target': { ko: '대상 User ID', en: 'Target User ID', ja: '対象 User ID' },
  'settings.admin_external.placeholder_target': { ko: '숫자 user ID', en: 'numeric user ID', ja: '数値 user ID' },
  'settings.admin_external.btn_load': { ko: '조회', en: 'Load', ja: '取得' },
  'settings.admin_external.btn_loading': { ko: '조회 중…', en: 'Loading…', ja: '取得中...' },
  'settings.admin_external.btn_save_override': {
    ko: '저장 (admin override)',
    en: 'Save (admin override)',
    ja: '保存 (admin override)',
  },
  'settings.admin_external.err_target_invalid': {
    ko: '숫자 user ID 를 입력하세요.',
    en: 'Please enter a numeric user ID.',
    ja: '数値の user ID を入力してください。',
  },
  'settings.admin_external.err_load': {
    ko: '조회 실패: {msg}',
    en: 'Load failed: {msg}',
    ja: '取得失敗: {msg}',
  },
  'settings.admin_external.msg_saved': {
    ko: 'user {id} 매핑 저장 완료',
    en: 'user {id} mapping saved',
    ja: 'user {id} のマッピングを保存しました',
  },

  // ----- AdminFaqSection -----
  'settings.admin_faq.heading': { ko: 'ADMIN — FAQ 관리', en: 'ADMIN — FAQ management', ja: 'ADMIN — FAQ 管理' },
  'settings.admin_faq.desc': {
    ko: 'ADMIN 권한 — FAQ 추가/비활성화. 비활성화는 soft delete (isActive=false), 데이터 보존.',
    en: 'ADMIN privileges — add/disable FAQs. Disable is a soft delete (isActive=false); data is retained.',
    ja: 'ADMIN 権限 — FAQ の追加/無効化。無効化は soft delete (isActive=false) でデータは保持。',
  },
  'settings.admin_faq.label_category': { ko: '카테고리', en: 'Category', ja: 'カテゴリ' },
  'settings.admin_faq.placeholder_category': {
    ko: '계정 / 결제 / 일반 등',
    en: 'e.g. Account / Billing / General',
    ja: '例: アカウント / 課金 / 一般',
  },
  'settings.admin_faq.label_question': { ko: '질문', en: 'Question', ja: '質問' },
  'settings.admin_faq.label_answer': { ko: '답변', en: 'Answer', ja: '回答' },
  'settings.admin_faq.label_sortorder': { ko: '정렬 순서', en: 'Sort order', ja: '並び順' },
  'settings.admin_faq.btn_add': { ko: 'FAQ 추가', en: 'Add FAQ', ja: 'FAQ を追加' },
  'settings.admin_faq.btn_adding': { ko: '추가 중…', en: 'Adding…', ja: '追加中...' },
  'settings.admin_faq.list_subtitle': {
    ko: '현재 FAQ 목록 ({count})',
    en: 'Current FAQ list ({count})',
    ja: '現在の FAQ 一覧 ({count})',
  },
  'settings.admin_faq.list_loading_count': { ko: '…', en: '…', ja: '…' },
  'settings.admin_faq.btn_disable': { ko: '비활성화', en: 'Disable', ja: '無効化' },
  'settings.admin_faq.aria_delete': {
    ko: '{question} 삭제',
    en: 'Delete {question}',
    ja: '{question} を削除',
  },
  'settings.admin_faq.confirm_disable': {
    ko: '이 FAQ 를 비활성화 하시겠습니까?',
    en: 'Disable this FAQ?',
    ja: 'この FAQ を無効化しますか?',
  },
  'settings.admin_faq.err_required': {
    ko: '카테고리, 질문, 답변 모두 필수입니다.',
    en: 'Category, question, and answer are all required.',
    ja: 'カテゴリ、質問、回答はすべて必須です。',
  },
  'settings.admin_faq.err_list': {
    ko: '목록 조회 실패: {msg}',
    en: 'Failed to load list: {msg}',
    ja: '一覧取得失敗: {msg}',
  },
  'settings.admin_faq.err_add': {
    ko: '추가 실패: {msg}',
    en: 'Add failed: {msg}',
    ja: '追加失敗: {msg}',
  },
  'settings.admin_faq.err_delete': {
    ko: '삭제 실패: {msg}',
    en: 'Delete failed: {msg}',
    ja: '削除失敗: {msg}',
  },
  'settings.admin_faq.msg_added': { ko: 'FAQ 추가됨', en: 'FAQ added', ja: 'FAQ を追加しました' },
  'settings.admin_faq.msg_disabled': { ko: 'FAQ 비활성화됨', en: 'FAQ disabled', ja: 'FAQ を無効化しました' },

  // ----- AdminBulkImportUserSection (CSV 일괄 등록) -----
  'settings.admin_import.heading': { ko: 'User 일괄 등록 (CSV)', en: 'Bulk import users (CSV)', ja: 'ユーザー一括登録 (CSV)' },
  'settings.admin_import.hint_columns_lead': { ko: 'CSV 컬럼:', en: 'CSV columns:', ja: 'CSV カラム:' },
  'settings.admin_import.hint_columns_required': { ko: '(필수)', en: '(required)', ja: '(必須)' },
  'settings.admin_import.hint_columns_optional': { ko: '(선택)', en: '(optional)', ja: '(任意)' },
  'settings.admin_import.hint_columns_default': {
    ko: ', default STAFF',
    en: ', default STAFF',
    ja: ', デフォルト STAFF',
  },
  'settings.admin_import.hint_password_optional': {
    ko: '(선택, 8자 이상)',
    en: '(optional, 8+ chars)',
    ja: '(任意、8文字以上)',
  },
  'settings.admin_import.hint_limit': {
    ko: '한 번에 최대 10,000 행 / 5 MB.',
    en: 'Up to 10,000 rows / 5 MB per upload.',
    ja: '1回あたり最大 10,000 行 / 5 MB。',
  },
  'settings.admin_import.hint_institution': {
    ko: '는 자동 (현재 ADMIN 의 기관).',
    en: 'is automatic (current ADMIN\'s institution).',
    ja: 'は自動 (現在の ADMIN の機関)。',
  },
  'settings.admin_import.hint_password_note': {
    ko: 'password 미설정 시 사용자는 별도 초기 비밀번호 발급 또는 invite 절차 필요 (login 차단 상태로 생성됨).',
    en: 'If password is unset, users will need a separate initial password issuance or invite (created with login blocked).',
    ja: 'password 未設定の場合、別途の初期パスワード発行または invite が必要 (login ブロック状態で作成)。',
  },
  'settings.admin_import.aria_file': { ko: 'CSV file', en: 'CSV file', ja: 'CSV ファイル' },
  'settings.admin_import.btn_upload': { ko: 'CSV 업로드', en: 'Upload CSV', ja: 'CSV アップロード' },
  'settings.admin_import.btn_uploading': { ko: '업로드 중…', en: 'Uploading…', ja: 'アップロード中...' },
  'settings.admin_import.err_file_size': {
    ko: '파일 크기 {mb} MB — 최대 5 MB',
    en: 'File size {mb} MB — max 5 MB',
    ja: 'ファイルサイズ {mb} MB — 最大 5 MB',
  },
  'settings.admin_import.err_no_file': {
    ko: 'CSV 파일을 선택해주세요.',
    en: 'Please select a CSV file.',
    ja: 'CSV ファイルを選択してください。',
  },
  'settings.admin_import.summary_lead': {
    ko: '총 {total}행',
    en: 'Total {total} rows',
    ja: '合計 {total} 行',
  },
  'settings.admin_import.summary_success_fail': {
    ko: '— 성공 {success} / 실패 {failure}',
    en: '— success {success} / failure {failure}',
    ja: '— 成功 {success} / 失敗 {failure}',
  },
  'settings.admin_import.aborted': { ko: ' (중단됨)', en: ' (aborted)', ja: ' (中断)' },
  'settings.admin_import.audit_failed': {
    ko: '⚠️ audit 기록 실패 — admin 이 별도 추적 필요 (server 로그 확인)',
    en: '⚠️ audit recording failed — admin must track separately (check server logs)',
    ja: '⚠️ audit 記録失敗 — admin が別途追跡必要 (server ログ参照)',
  },
  'settings.admin_import.summary_aria': { ko: '오류 코드 빈도 상위', en: 'Top error code frequency', ja: 'エラーコード頻度 上位' },
  'settings.admin_import.summary_title': {
    ko: '오류 요약 (상위 {n}):',
    en: 'Error summary (top {n}):',
    ja: 'エラー要約 (上位 {n}):',
  },
  'settings.admin_import.summary_count_suffix': { ko: '건', en: '', ja: '件' },
  'settings.admin_import.table.col_row': { ko: '행', en: 'Row', ja: '行' },
  'settings.admin_import.table.col_email': { ko: 'email', en: 'email', ja: 'email' },
  'settings.admin_import.table.col_code': { ko: 'code', en: 'code', ja: 'code' },
  'settings.admin_import.table.col_message': { ko: '메시지', en: 'Message', ja: 'メッセージ' },
  'settings.admin_import.overflow_all_warn': {
    ko: '전체 {n}건 표시 중 — DOM 무게 주의 (브라우저가 느려질 수 있음).',
    en: 'Showing all {n} — DOM weight warning (browser may slow).',
    ja: '全 {n} 件表示中 — DOM 重量に注意 (ブラウザが遅くなる可能性)。',
  },
  'settings.admin_import.overflow_show_top': {
    ko: '상위 {n}건만 보기',
    en: 'Show only top {n}',
    ja: '上位 {n} 件のみ表示',
  },
  'settings.admin_import.overflow_top_with_hidden': {
    ko: '상위 {visible}건 표시 — 외 {hidden}건 생략 (빈도 요약과 server audit 참조).',
    en: 'Showing top {visible} — {hidden} more hidden (see frequency summary and server audit).',
    ja: '上位 {visible} 件表示 — 残り {hidden} 件省略 (頻度要約と server audit を参照)。',
  },
  'settings.admin_import.overflow_show_all': {
    ko: '전체 {n}건 보기',
    en: 'Show all {n}',
    ja: '全 {n} 件を表示',
  },
  // ----- AdminBulkImportUserSection — 14 code hints + action -----
  // 도메인 한국어 텍스트가 backend code 와 의미 결합 ↑. ko/en/ja 모두 동등 표현 권장.
  'settings.admin_import.code.EMAIL_MISSING': {
    ko: '해당 행에 email 컬럼 채워서 재업로드',
    en: 'Fill the email column for this row and re-upload',
    ja: '該当行の email カラムを埋めて再アップロード',
  },
  'settings.admin_import.code.NAME_MISSING': {
    ko: '해당 행에 name 컬럼 채워서 재업로드',
    en: 'Fill the name column for this row and re-upload',
    ja: '該当行の name カラムを埋めて再アップロード',
  },
  'settings.admin_import.code.EMAIL_INVALID': {
    ko: 'consecutive dot / multi-@ / 시작·끝 dot 제거 후 재업로드',
    en: 'Remove consecutive dots / multi-@ / leading/trailing dots and re-upload',
    ja: '連続ドット / 複数 @ / 先頭末尾ドットを除去して再アップロード',
  },
  'settings.admin_import.code.EMAIL_TOO_LONG': {
    ko: '짧은 email 으로 변경 (최대 255자)',
    en: 'Use a shorter email (max 255 chars)',
    ja: '短い email に変更 (最大 255 文字)',
  },
  'settings.admin_import.code.NAME_TOO_LONG': {
    ko: '짧은 name 으로 변경 (최대 50자)',
    en: 'Use a shorter name (max 50 chars)',
    ja: '短い name に変更 (最大 50 文字)',
  },
  'settings.admin_import.code.EMPLOYEE_NUMBER_TOO_LONG': {
    ko: '짧은 사원번호로 변경 (최대 32자)',
    en: 'Use a shorter employee number (max 32 chars)',
    ja: '短い社員番号に変更 (最大 32 文字)',
  },
  'settings.admin_import.code.ROLE_INVALID': {
    ko: 'role 컬럼을 ADMIN/MANAGER/STAFF 중 하나로 수정',
    en: 'Set role column to one of ADMIN/MANAGER/STAFF',
    ja: 'role カラムを ADMIN/MANAGER/STAFF のいずれかに修正',
  },
  'settings.admin_import.code.PASSWORD_TOO_SHORT': {
    ko: '8자 이상으로 변경 또는 비워두기 (login 차단으로 생성)',
    en: 'Use 8+ chars or leave blank (created with login blocked)',
    ja: '8 文字以上に変更または空欄 (login ブロック状態で作成)',
  },
  'settings.admin_import.code.PASSWORD_HASH_FAILED': {
    ko: 'bcrypt 처리 실패 — password 형식 재확인 또는 server 로그 참조',
    en: 'bcrypt failed — re-check password format or see server logs',
    ja: 'bcrypt 処理失敗 — password 形式を再確認、または server ログ参照',
  },
  'settings.admin_import.code.DUPLICATE_IN_CSV': {
    ko: 'CSV 안 중복 행 제거 후 재업로드',
    en: 'Remove duplicate rows in the CSV and re-upload',
    ja: 'CSV 内の重複行を削除して再アップロード',
  },
  'settings.admin_import.code.EMAIL_EXISTS': {
    ko: '추가 작업 불필요 — 기존 사용자 유지 (skip)',
    en: 'No action needed — existing user retained (skip)',
    ja: '追加作業不要 — 既存ユーザーを維持 (skip)',
  },
  'settings.admin_import.code.DELETED_EMAIL_EXISTS': {
    ko: '이전 사용자 (탈퇴) 가 동일 email 보유. hard-delete 후 재등록 필요',
    en: 'A previously deleted user has the same email. Hard-delete then re-register.',
    ja: '以前のユーザー (退会済) が同一 email を保持。hard-delete 後に再登録が必要',
  },
  'settings.admin_import.code.INSTITUTION_NOT_FOUND': {
    ko: 'institution 비존재·비활성 — admin 의 institution 확인',
    en: 'Institution missing or inactive — check the admin\'s institution',
    ja: 'institution 不在・非活性 — admin の institution を確認',
  },
  'settings.admin_import.code.FK_VIOLATION': {
    ko: 'foreign key 제약 위반 — server 로그 참조',
    en: 'Foreign key violation — see server logs',
    ja: '外部キー制約違反 — server ログ参照',
  },
  'settings.admin_import.code.CREATE_FAILED': {
    ko: 'create 실패 — server 로그 참조',
    en: 'Create failed — see server logs',
    ja: 'create 失敗 — server ログ参照',
  },
  'settings.admin_import.code.UNKNOWN': {
    ko: '서버 응답 코드 — 클라이언트 미정의 (서버 업데이트 후 frontend 갱신 필요)',
    en: 'Server response code — undefined in client (frontend update required after server update)',
    ja: 'サーバー応答コード — クライアント未定義 (サーバー更新後に frontend 更新が必要)',
  },
  'settings.admin_import.action.manual': {
    ko: '관리자 매뉴얼 참조',
    en: 'See admin manual',
    ja: '管理者マニュアル参照',
  },
  'settings.admin_import.action.server_log': {
    ko: 'server 로그 확인',
    en: 'Check server logs',
    ja: 'server ログ確認',
  },
};

export type SettingsI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 59 — SettingsPage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * params 치환 ({name}) — calendar / workspace / mail / onboarding / board / task 와 일관.
 */
export function tSettings(key: SettingsI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getSettingsLang(): Lang { return getSharedLang(); }
export function setSettingsLang(value: Lang): void { setSharedLang(value); }
export function subscribeSettingsLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
